import chiSquaredTest from "chi-squared-test";
//import ttest from "ttest"
import * as Collections from "./collect";
import { isEnabled } from "./featureFlags";
function isObjectsEqual(obj1, obj2) {
    if (obj1 === undefined || obj2 === undefined) {
        return obj1 === obj2;
    }
    const keys1 = Object.keys(obj1);
    if (keys1.length != Object.keys(obj2).length) {
        return false;
    }
    if (keys1.length == 0) {
        return true;
    }
    return keys1.every((key) => obj1[key] == obj2[key]);
}
export function logit(x) {
    return Math.log(x / (1 - x));
}
export function sigmoid(x) {
    const exp = Math.exp(x);
    if (!isFinite(exp)) {
        return x > 0 ? 1 : 0;
    }
    return exp / (1 + exp);
}
export function getSummary(x) {
    return DefaultStatisticalSummary.fromValues(x);
}
export class DefaultStatisticalSummary {
    n;
    missingN;
    sum;
    sumOfSquares;
    constructor(n, missingN, sum = n, sumOfSquares = sum) {
        this.n = n;
        this.missingN = missingN;
        this.sum = sum;
        this.sumOfSquares = sumOfSquares;
    }
    getMean() {
        return this.sum / this.n;
    }
    getN() {
        return this.n;
    }
    getMissingN() {
        return this.missingN ?? 0;
    }
    getTotalN() {
        return this.getN() + this.getMissingN();
    }
    getStandardDeviation() {
        return Math.sqrt(this.getVariance());
    }
    getSum() {
        return this.sum;
    }
    getSumOfSquares() {
        return this.sumOfSquares;
    }
    getTotalSumOfSquares() {
        return this.n === 0 ? 0 : this.sumOfSquares - (this.sum * this.sum) / this.n;
    }
    getVariance() {
        return this.getTotalSumOfSquares() / (this.n - 1);
    }
    subtract(summary) {
        if (summary.n == 0) {
            return this;
        }
        const n = this.n - summary.getN();
        const missingN = this.missingN === undefined ? this.missingN : this.getMissingN() - summary.getMissingN();
        const sum = this.sum - summary.getSum();
        const sumOfSquares = this.sumOfSquares - summary.getSumOfSquares();
        if (this.sum == this.sumOfSquares && summary.getSum() == summary.getSumOfSquares()) {
            return new DefaultStatisticalSummary(Math.max(n, 0), missingN !== undefined ? Math.max(missingN, 0) : undefined, Math.max(sum, 0), Math.max(sumOfSquares, 0));
        }
        else if (n <= 0) {
            return new DefaultStatisticalSummary(0, 0, 0, 0);
        }
        else {
            return new DefaultStatisticalSummary(n, missingN, sum, sumOfSquares);
        }
    }
    add(summary) {
        if (summary.n == 0) {
            return this;
        }
        return DefaultStatisticalSummary.addAll(this, summary);
    }
    static addAll(...summaries) {
        let n = 0, nMissing = 0, sum = 0, sumOfSquares = 0;
        for (let i = 0; i < summaries.length; i++) {
            n += summaries[i].n;
            nMissing += summaries[i].getMissingN();
            sum += summaries[i].sum;
            sumOfSquares += summaries[i].sumOfSquares;
        }
        if (summaries.length > 0 && summaries[0].missingN === undefined) {
            nMissing = undefined;
        }
        return new DefaultStatisticalSummary(n, nMissing, sum, sumOfSquares);
    }
    static fromValues(x) {
        const n = x.length;
        let sum = 0, sumOfSquares = 0;
        for (let i = 0; i < x.length; i++) {
            sum += x[i];
            sumOfSquares += x[i] * x[i];
        }
        return new DefaultStatisticalSummary(n, 0, sum, sumOfSquares);
    }
}
export class CategoricalDifferenceTester {
    static INSTANCE = new CategoricalDifferenceTester();
    isDifferent(summary, other) {
        const total = summary.add(other);
        const observed = [
            summary.getSum(),
            summary.getN() - summary.getSum(),
            other.getSum(),
            other.getN() - other.getSum()
        ];
        const expected = [
            summary.getN() * total.getMean(),
            summary.getN() * (1 - total.getMean()),
            other.getN() * total.getMean(),
            other.getN() * (1 - total.getMean())
        ];
        return chiSquaredTest(observed, expected, 1).probability < 0.1;
    }
}
export class NumericDifferenceTester {
    static INSTANCE = new NumericDifferenceTester();
    isDifferent(summary, other) {
        const left = {
            mean: summary.getMean(),
            variance: summary.getVariance(),
            size: summary.getN()
        };
        const right = {
            mean: other.getMean(),
            variance: other.getVariance(),
            size: other.getN()
        };
        const options = {
            alpha: 0.1
        };
        throw new Error("not yet implemented");
        //return ttest(left, right, options).valid();
    }
}
const ROUND = isEnabled("integer_confusion_matrix_values");
export const roundIfConfigured = ROUND ? Math.round : (x) => x;
export function positiveRate(tp, tn, fp, fn) {
    return (tp + fp) / (tp + tn + fp + fn);
}
export function truePositiveRate(tp, tn, fp, fn) {
    return tp / (tp + fn);
}
export function sensitivity(tp, tn, fp, fn) {
    return truePositiveRate(tp, tn, fp, fn);
}
export function recall(tp, tn, fp, fn) {
    return truePositiveRate(tp, tn, fp, fn);
}
export function hitRate(tp, tn, fp, fn) {
    return truePositiveRate(tp, tn, fp, fn);
}
export function trueNegativeRate(tp, tn, fp, fn) {
    return tn / (tn + fp);
}
export function specificity(tp, tn, fp, fn) {
    return trueNegativeRate(tp, tn, fp, fn);
}
export function selectivity(tp, tn, fp, fn) {
    return trueNegativeRate(tp, tn, fp, fn);
}
export function positivePredictiveValue(tp, tn, fp, fn) {
    return tp / (tp + fp);
}
export function precision(tp, tn, fp, fn) {
    return positivePredictiveValue(tp, tn, fp, fn);
}
export function negativePredictiveValue(tp, tn, fp, fn) {
    return tn / (tn + fn);
}
export function falseNegativeRate(tp, tn, fp, fn) {
    return fn / (fn + tp);
}
export function missRate(tp, tn, fp, fn) {
    return falseNegativeRate(tp, tn, fp, fn);
}
export function falsePositiveRate(tp, tn, fp, fn) {
    return fp / (fp + tn);
}
export function fallOut(tp, tn, fp, fn) {
    return falsePositiveRate(tp, tn, fp, fn);
}
export function falseDiscoveryRate(tp, tn, fp, fn) {
    return fp / (fp + tp);
}
export function falseOmissionRate(tp, tn, fp, fn) {
    return fn / (fn + tn);
}
export function criticalSuccessIndex(tp, tn, fp, fn) {
    return tp / (tp + fn + fp);
}
export function accuracy(tp, tn, fp, fn) {
    return (tp + tn) / (tp + tn + fp + fn);
}
export function fScore(tp, tn, fp, fn, beta) {
    const p = precision(tp, tn, fp, fn);
    const r = recall(tp, tn, fp, fn);
    const betaSquared = beta * beta;
    return ((1 + betaSquared) * (p * r)) / (betaSquared * p + r);
}
export function f1Score(tp, tn, fp, fn) {
    return fScore(tp, tn, fp, fn, 1);
}
export function matthewsCorrelationCoefficient(tp, tn, fp, fn) {
    const denominator = Math.sqrt((tp + fp) * (tp + fn) * (tn + fp) * (tn + fn));
    return (tp * tn - fp * fn) / (denominator == 0 ? 1 : denominator);
}
export function informedness(tp, tn, fp, fn) {
    return truePositiveRate(tp, tn, fp, fn) + trueNegativeRate(tp, tn, fp, fn) - 1;
}
export function markedness(tp, tn, fp, fn) {
    return positivePredictiveValue(tp, tn, fp, fn) + negativePredictiveValue(tp, tn, fp, fn) - 1;
}
export var ClassificationMetric;
(function (ClassificationMetric) {
    ClassificationMetric["PositiveRate"] = "positive_rate";
    ClassificationMetric["TruePositiveRate"] = "true_positive_rate";
    ClassificationMetric["Sensitivity"] = "sensitivity";
    ClassificationMetric["Recall"] = "recall";
    ClassificationMetric["HitRate"] = "hit_rate";
    ClassificationMetric["TrueNegativeRate"] = "true_negative_rate";
    ClassificationMetric["Specificity"] = "specificity";
    ClassificationMetric["Selectivity"] = "selectivity";
    ClassificationMetric["PositivePredictiveValue"] = "positive_predictive_value";
    ClassificationMetric["Precision"] = "precision";
    ClassificationMetric["NegativePredictiveValue"] = "negative_predictive_value";
    ClassificationMetric["FalseNegativeRate"] = "false_negative_rate";
    ClassificationMetric["MissRate"] = "miss_rate";
    ClassificationMetric["FalsePositiveRate"] = "false_positive_rate";
    ClassificationMetric["FallOut"] = "fall_out";
    ClassificationMetric["FalseDiscoveryRate"] = "false_discovery_rate";
    ClassificationMetric["FalseOmissionRate"] = "false_omission_rate";
    ClassificationMetric["CriticalSuccessIndex"] = "critical_success_index";
    ClassificationMetric["Accuracy"] = "accuracy";
    ClassificationMetric["F1Score"] = "f1_score";
    ClassificationMetric["MatthewsCorrelationCoefficient"] = "matthews_correlation_coefficient";
    ClassificationMetric["Informedness"] = "informedness";
    ClassificationMetric["Markedness"] = "markedness";
})(ClassificationMetric || (ClassificationMetric = {}));
(function (ClassificationMetric) {
    const reverseMap = new Map();
    Object.keys(ClassificationMetric).forEach((s) => {
        const e = ClassificationMetric[s];
        reverseMap.set(e.toString(), e);
    });
    function valueOf(str) {
        return reverseMap.get(str);
    }
    ClassificationMetric.valueOf = valueOf;
    function toMapper(metric) {
        switch (metric) {
            case ClassificationMetric.PositiveRate:
                return (cm) => cm.getPositiveRate();
            case ClassificationMetric.TruePositiveRate:
                return (cm) => cm.getTruePositiveRate();
            case ClassificationMetric.Sensitivity:
                return (cm) => cm.getSensitivity();
            case ClassificationMetric.Recall:
                return (cm) => cm.getRecall();
            case ClassificationMetric.HitRate:
                return (cm) => cm.getHitRate();
            case ClassificationMetric.TrueNegativeRate:
                return (cm) => cm.getTrueNegativeRate();
            case ClassificationMetric.Specificity:
                return (cm) => cm.getSpecificity();
            case ClassificationMetric.Selectivity:
                return (cm) => cm.getSelectivity();
            case ClassificationMetric.PositivePredictiveValue:
                return (cm) => cm.getPositivePredictiveValue();
            case ClassificationMetric.Precision:
                return (cm) => cm.getPrecision();
            case ClassificationMetric.NegativePredictiveValue:
                return (cm) => cm.getNegativePredictiveValue();
            case ClassificationMetric.FalseNegativeRate:
                return (cm) => cm.getFalseNegativeRate();
            case ClassificationMetric.MissRate:
                return (cm) => cm.getMissRate();
            case ClassificationMetric.FalsePositiveRate:
                return (cm) => cm.getFalsePositiveRate();
            case ClassificationMetric.FallOut:
                return (cm) => cm.getFallOut();
            case ClassificationMetric.FalseDiscoveryRate:
                return (cm) => cm.getFalseDiscoveryRate();
            case ClassificationMetric.FalseOmissionRate:
                return (cm) => cm.getFalseOmissionRate();
            case ClassificationMetric.CriticalSuccessIndex:
                return (cm) => cm.getCriticalSuccessIndex();
            case ClassificationMetric.Accuracy:
                return (cm) => cm.getAccuracy();
            case ClassificationMetric.F1Score:
                return (cm) => cm.getF1Score();
            case ClassificationMetric.MatthewsCorrelationCoefficient:
                return (cm) => cm.getMatthewsCorrelationCoefficient();
            case ClassificationMetric.Informedness:
                return (cm) => cm.getInformedness();
            case ClassificationMetric.Markedness:
                return (cm) => cm.getMarkedness();
            default:
                return (cm) => NaN;
        }
    }
    ClassificationMetric.toMapper = toMapper;
})(ClassificationMetric || (ClassificationMetric = {}));
export class CostBenefitMatrix {
    truePositiveValue;
    trueNegativeValue;
    falsePositiveValue;
    falseNegativeValue;
    constructor(truePositiveValue, trueNegativeValue, falsePositiveValue, falseNegativeValue) {
        this.truePositiveValue = truePositiveValue;
        this.trueNegativeValue = trueNegativeValue;
        this.falsePositiveValue = falsePositiveValue;
        this.falseNegativeValue = falseNegativeValue;
    }
    equals(other) {
        return (other !== undefined &&
            this.truePositiveValue == other.truePositiveValue &&
            this.trueNegativeValue == other.trueNegativeValue &&
            this.falsePositiveValue == other.falsePositiveValue &&
            this.falseNegativeValue == other.falseNegativeValue);
    }
    getBreakEvenProb() {
        return (-1 * this.falsePositiveValue) / (-1 * this.falsePositiveValue + this.truePositiveValue);
    }
}
export class ConfusionMatrix {
    tp;
    tn;
    fp;
    fn;
    threshold;
    constructor(tp, tn, fp, fn, threshold) {
        this.tp = tp;
        this.tn = tn;
        this.fp = fp;
        this.fn = fn;
        this.threshold = threshold;
    }
    _copy(tp, tn, fp, fn) {
        return new ConfusionMatrix(tp, tn, fp, fn, this.threshold);
    }
    equals(other) {
        return (other !== undefined && this.tp == other.tp && this.tn == other.tn && this.fp == other.fp && this.fn == other.fn);
    }
    getTruePositive() {
        return this.tp;
    }
    getTrueNegative() {
        return this.tn;
    }
    getFalsePositive() {
        return this.fp;
    }
    getFalseNegative() {
        return this.fn;
    }
    getTotal() {
        return this.tp + this.tn + this.fp + this.fn;
    }
    getAllPositive() {
        return this.tp + this.fp;
    }
    getAllNegative() {
        return this.tn + this.fn;
    }
    getActualPositive() {
        return this.tp + this.fn;
    }
    getActualNegative() {
        return this.tn + this.fp;
    }
    getExpectedValue(truePositiveValue, trueNegativeValue, falsePositiveValue, falseNegativeValue) {
        const cbm = new CostBenefitMatrix(truePositiveValue, trueNegativeValue, falsePositiveValue, falseNegativeValue);
        return this.getImpact(cbm);
    }
    getImpact(cbm) {
        return (this.tp * cbm.truePositiveValue +
            this.tn * cbm.trueNegativeValue +
            this.fp * cbm.falsePositiveValue +
            this.fn * cbm.falseNegativeValue);
    }
    combineMatrices(cm) {
        const tp = this.tp + cm.tp;
        const tn = this.tn + cm.tn;
        const fp = this.fp + cm.fp;
        const fn = this.fn + cm.fn;
        return this._copy(tp, tn, fp, fn);
    }
    copyAndScaleTotal(total) {
        const scale = total / this.getTotal();
        if (scale === 1) {
            return this;
        }
        const tp = roundIfConfigured(this.tp * scale);
        const tn = roundIfConfigured(this.tn * scale);
        const fp = roundIfConfigured(this.fp * scale);
        const fn = Math.max(total - tp - tn - fp, 0);
        return this._copy(tp, tn, fp, fn);
    }
    copyAndConstrainPositivePredictions(capacity) {
        if (capacity >= this.getAllPositive()) {
            return this;
        }
        const truncatedCapacity = Math.max(0, roundIfConfigured(capacity));
        const p = this.getPrecision();
        const constrainedTruePositives = truncatedCapacity * (isFinite(p) ? p : 0);
        const constrainedFalsePositives = truncatedCapacity - constrainedTruePositives;
        const updatedTrueNegatives = this.tn + (this.fp - constrainedFalsePositives);
        const updatedFalseNegatives = this.fn + (this.tp - constrainedTruePositives);
        return this._copy(constrainedTruePositives, updatedTrueNegatives, constrainedFalsePositives, updatedFalseNegatives);
    }
    copyAndConstrainActualPositives(constraint) {
        if (constraint > this.getTotal()) {
            return this;
        }
        const newPositives = Math.max(0, roundIfConfigured(constraint));
        let updatedTruePositives, updatedFalsePositives, updatedTrueNegatives, updatedFalseNegatives;
        if (newPositives <= this.getActualPositive()) {
            const positiveRatio = newPositives / (this.tp + this.fn || 1);
            updatedTruePositives = roundIfConfigured(this.tp * positiveRatio);
            updatedFalseNegatives = newPositives - updatedTruePositives;
            updatedFalsePositives = this.fp + this.tp - updatedTruePositives;
            updatedTrueNegatives = this.tn + this.fn - updatedFalseNegatives;
        }
        else {
            const newNegatives = this.getTotal() - newPositives;
            const negativeRatio = newNegatives / (this.fp + this.tn || 1);
            updatedFalsePositives = roundIfConfigured(this.fp * negativeRatio);
            updatedTrueNegatives = newNegatives - updatedFalsePositives;
            updatedTruePositives = this.fp + this.tp - updatedFalsePositives;
            updatedFalseNegatives = this.fn + this.tn - updatedTrueNegatives;
        }
        return this._copy(updatedTruePositives, updatedTrueNegatives, updatedFalsePositives, updatedFalseNegatives);
    }
    copyAndScaleLegacy(total, capacity) {
        const recall = this.getRecall();
        const precision = this.getPrecision();
        const truePositive = roundIfConfigured(recall * capacity);
        const falseNegative = capacity - truePositive;
        const falsePositive = Math.min(total - truePositive - falseNegative, isNaN(precision) || precision == 0 ? 0 : roundIfConfigured(truePositive / precision - truePositive));
        const trueNegative = total - truePositive - falsePositive - falseNegative;
        return this._copy(truePositive, trueNegative, falsePositive, falseNegative);
    }
    copyAndScaleLegacyUpdated(total, totalPossibleHits) {
        if (total === undefined) {
            total = this.getTotal();
        }
        const cm = this.copyAndScaleTotal(total);
        if (totalPossibleHits === undefined) {
            return cm;
        }
        const currentActualPositive = cm.getActualPositive();
        totalPossibleHits = Math.min(totalPossibleHits, total);
        if (totalPossibleHits == currentActualPositive) {
            return cm;
        }
        else if (totalPossibleHits < currentActualPositive) {
            const recall = cm.getRecall();
            const truePositive = roundIfConfigured(totalPossibleHits * (isFinite(recall) ? recall : 0));
            const falseNegative = totalPossibleHits - truePositive;
            const falsePositive = cm.getAllPositive() - truePositive;
            const trueNegative = Math.max(0, total - truePositive - falsePositive - falseNegative);
            return this._copy(truePositive, trueNegative, falsePositive, falseNegative);
        }
        else {
            const fpr = cm.getFalsePositiveRate();
            const totalPossibleMisses = total - totalPossibleHits;
            const falsePositive = roundIfConfigured(totalPossibleMisses * (isFinite(fpr) ? fpr : 0));
            const trueNegative = totalPossibleMisses - falsePositive;
            const truePositive = cm.getAllPositive() - falsePositive;
            const falseNegative = Math.max(0, total - truePositive - falsePositive - trueNegative);
            return this._copy(truePositive, trueNegative, falsePositive, falseNegative);
        }
    }
    getPositiveRate() {
        return positiveRate(this.tp, this.tn, this.fp, this.fn);
    }
    getTruePositiveRate() {
        return truePositiveRate(this.tp, this.tn, this.fp, this.fn);
    }
    getSensitivity() {
        return sensitivity(this.tp, this.tn, this.fp, this.fn);
    }
    getRecall() {
        return recall(this.tp, this.tn, this.fp, this.fn);
    }
    getHitRate() {
        return hitRate(this.tp, this.tn, this.fp, this.fn);
    }
    getTrueNegativeRate() {
        return trueNegativeRate(this.tp, this.tn, this.fp, this.fn);
    }
    getSpecificity() {
        return specificity(this.tp, this.tn, this.fp, this.fn);
    }
    getSelectivity() {
        return selectivity(this.tp, this.tn, this.fp, this.fn);
    }
    getPositivePredictiveValue() {
        return positivePredictiveValue(this.tp, this.tn, this.fp, this.fn);
    }
    getPrecision() {
        return precision(this.tp, this.tn, this.fp, this.fn);
    }
    getNegativePredictiveValue() {
        return negativePredictiveValue(this.tp, this.tn, this.fp, this.fn);
    }
    getFalseNegativeRate() {
        return falseNegativeRate(this.tp, this.tn, this.fp, this.fn);
    }
    getMissRate() {
        return missRate(this.tp, this.tn, this.fp, this.fn);
    }
    getFalsePositiveRate() {
        return falsePositiveRate(this.tp, this.tn, this.fp, this.fn);
    }
    getFallOut() {
        return fallOut(this.tp, this.tn, this.fp, this.fn);
    }
    getFalseDiscoveryRate() {
        return falseDiscoveryRate(this.tp, this.tn, this.fp, this.fn);
    }
    getFalseOmissionRate() {
        return falseOmissionRate(this.tp, this.tn, this.fp, this.fn);
    }
    getCriticalSuccessIndex() {
        return criticalSuccessIndex(this.tp, this.tn, this.fp, this.fn);
    }
    getAccuracy() {
        return accuracy(this.tp, this.tn, this.fp, this.fn);
    }
    getFScore(beta) {
        return fScore(this.tp, this.tn, this.fp, this.fn, beta);
    }
    getF1Score() {
        return f1Score(this.tp, this.tn, this.fp, this.fn);
    }
    getMatthewsCorrelationCoefficient() {
        return matthewsCorrelationCoefficient(this.tp, this.tn, this.fp, this.fn);
    }
    getInformedness() {
        return informedness(this.tp, this.tn, this.fp, this.fn);
    }
    getMarkedness() {
        return markedness(this.tp, this.tn, this.fp, this.fn);
    }
    getMetric(metric) {
        const mapper = ClassificationMetric.toMapper(metric);
        return mapper(this);
    }
    getMetricByName(name) {
        const metric = ClassificationMetric.valueOf(name);
        if (metric !== undefined) {
            return this.getMetric(metric);
        }
        return undefined;
    }
}
class ScoreRangeCounts {
    score;
    actualPositive;
    actualNegative;
    constructor(score, actualPositive, actualNegative) {
        this.score = score;
        this.actualPositive = actualPositive;
        this.actualNegative = actualNegative;
    }
    copyAndAdjust(transform = (x) => x) {
        const transformedScore = transform(this.score);
        const delta = transformedScore - this.score;
        const total = this.actualPositive + this.actualNegative;
        const shift = total * Math.abs(delta);
        let adjustedActualPositive = -1, adjustedActualNegative = -1;
        if (delta == 0) {
            return this;
        }
        else if (delta < 0) {
            adjustedActualNegative = this.actualNegative + shift;
            adjustedActualPositive = total - adjustedActualNegative;
        }
        else if (delta > 0) {
            adjustedActualPositive = this.actualPositive + shift;
            adjustedActualNegative = total - adjustedActualPositive;
        }
        return new ScoreRangeCounts(transformedScore, adjustedActualPositive, adjustedActualNegative);
    }
    static fromHistogramValues(scoreRangeString, actualPositive, actualNegative) {
        // Example score range "[0.0, 0.01)"
        const split = scoreRangeString.split(", ");
        const score = Number(split[0].substring(1));
        return new ScoreRangeCounts(score, actualPositive, actualNegative);
    }
    static manyFromRun(run) {
        let scores = [];
        if (run.matrices.length == 0 ||
            run.metadata.metrics === undefined ||
            run.metadata.metrics.prediction_histogram === undefined) {
            if (run.matrices.length > 0) {
                const tp = run.matrices[0][3];
                const fp = run.matrices[0][1];
                const tn = run.matrices[0][2];
                const fn = run.matrices[0][0];
                scores = [new ScoreRangeCounts(0, fn, tn), new ScoreRangeCounts(0.5, tp, fp)];
            }
        }
        else {
            const hist = run.metadata.metrics.prediction_histogram;
            scores = Object.keys(hist.actual_negative)
                .map((range) => ScoreRangeCounts.fromHistogramValues(range, hist.actual_positive[range], hist.actual_negative[range]))
                .sort(Collections.getNumericComparator((x) => x.score));
        }
        if (scores.length > 0) {
            const last = scores[scores.length - 1];
            // force in the guess everything false case
            scores = [...scores, new ScoreRangeCounts(last.score < 1 ? 1 : 1.1, 0, 0)];
        }
        return scores;
    }
}
export class ConfusionMatrixFactoryFactory {
    successRate;
    scoreCounts;
    lastCBM;
    lastPopulationSize;
    lastPopulationSuccesses;
    lastArgs;
    cachedCMF;
    constructor(successRate, scoreCounts) {
        this.successRate = successRate;
        this.scoreCounts = scoreCounts;
    }
    getConfusionMatrixFactory(populationSize, populationSuccesses, cbm) {
        if (cbm !== undefined &&
            cbm.equals(this.lastCBM) &&
            populationSize === this.lastPopulationSize &&
            populationSuccesses === this.lastPopulationSuccesses &&
            this.cachedCMF !== undefined) {
            return this.cachedCMF;
        }
        else {
            this.lastCBM = cbm;
            this.lastPopulationSize = populationSize;
            this.lastPopulationSuccesses = populationSuccesses;
        }
        const populationSuccessRate = populationSize !== undefined && populationSuccesses !== undefined
            ? populationSize / populationSuccesses
            : undefined;
        const transform = this._getScoreTransform(populationSuccessRate);
        const adjustedScoreCounts = this.scoreCounts.map(
        // TODO: make copyAndAdjust work
        (score) => score //score.copyAndAdjust(transform)
        );
        const thresholds = [];
        const cumulativeActualPositives = [];
        const cumulativeActualNegatives = [];
        let totalActualPositive = 0;
        let totalActualNegative = 0;
        for (let i = adjustedScoreCounts.length; --i >= 0;) {
            const count = adjustedScoreCounts[i];
            totalActualPositive += count.actualPositive;
            totalActualNegative += count.actualNegative;
            thresholds.unshift(count.score);
            cumulativeActualPositives.unshift(totalActualPositive);
            cumulativeActualNegatives.unshift(totalActualNegative);
        }
        const confusionMatrices = [];
        for (let i = 0; i < thresholds.length; i++) {
            const truePositive = cumulativeActualPositives[i];
            const falsePositive = cumulativeActualNegatives[i];
            const trueNegative = totalActualNegative - falsePositive;
            const falseNegative = totalActualPositive - truePositive;
            const cm = new ConfusionMatrix(truePositive, trueNegative, falsePositive, falseNegative, thresholds[i]).copyAndScaleLegacyUpdated(populationSize, populationSuccesses);
            confusionMatrices.push(cm);
        }
        const cmf = ConfusionMatrixFactory.create(confusionMatrices, cbm);
        this.cachedCMF = cmf;
        return cmf;
    }
    _getScoreTransform(populationSuccessRate) {
        if (populationSuccessRate === undefined || populationSuccessRate === this.successRate) {
            return (score) => score;
        }
        const y = this.successRate;
        const r = populationSuccessRate;
        const shift = logit(r) - logit(y);
        return (score) => sigmoid(logit(score) + shift);
    }
    static fromRun(run) {
        const counts = ScoreRangeCounts.manyFromRun(run);
        const totalActualPositive = counts.map((count) => count.actualPositive).reduce((x, y) => x + y, 0);
        const totalActualNegative = counts.map((count) => count.actualNegative).reduce((x, y) => x + y, 0);
        const successRate = totalActualPositive / (totalActualPositive + totalActualNegative);
        return new ConfusionMatrixFactoryFactory(successRate, counts);
    }
}
export class ConfusionMatrixFactory {
    // Factory for creating Confusion Matrices
    confusionMatrices;
    defaultConfusionMatrix;
    cache = {};
    constructor(confusionMatrices, defaultConfusionMatrix) {
        this.confusionMatrices = confusionMatrices;
        this.defaultConfusionMatrix = defaultConfusionMatrix;
    }
    _getInBounds(array, index) {
        if (index < 0) {
            index = 0;
        }
        else if (index >= array.length) {
            index = array.length - 1;
        }
        return array[index];
    }
    _getConfusionMatrix(index) {
        return this._getInBounds(this.confusionMatrices, index);
    }
    static _adjustToCapacity(lower, upper, capacity, interpolate = true) {
        const currentCapacity = lower.getAllPositive();
        const nextCapacity = upper.getAllPositive();
        if (capacity <= currentCapacity || currentCapacity == nextCapacity) {
            return lower;
        }
        else if (!interpolate || capacity >= nextCapacity) {
            return upper;
        }
        const weight = (capacity - currentCapacity) / (nextCapacity - currentCapacity);
        let truePositive = lower.getTruePositive();
        let falsePositive = lower.getFalsePositive();
        const marginalTruePositive = upper.getTruePositive() - truePositive;
        const marginalFalsePositive = upper.getFalsePositive() - falsePositive;
        truePositive += roundIfConfigured(marginalTruePositive * weight);
        falsePositive += roundIfConfigured(marginalFalsePositive * weight);
        const trueNegative = lower.getActualNegative() - falsePositive;
        const falseNegative = lower.getActualPositive() - truePositive;
        return new ConfusionMatrix(truePositive, trueNegative, falsePositive, falseNegative, lower.threshold);
    }
    getConfusionMatrix(config = {}) {
        if (config.threshold === undefined && config.capacity === undefined) {
            if (this.defaultConfusionMatrix !== undefined) {
                return this.defaultConfusionMatrix;
            }
            else {
                config = {
                    threshold: 0.5
                };
            }
        }
        if (config.threshold === undefined && config.capacity !== undefined && this.cache[config.capacity] !== undefined) {
            return this.cache[config.capacity];
        }
        let lower = 0;
        if (config.threshold !== undefined) {
            lower = Collections.binarySearchNumeric(this.confusionMatrices.map((cm) => cm.threshold ?? 0.5), config.threshold, Collections.SearchMode.NearestAbove);
        }
        if (config.capacity === undefined) {
            return this._getConfusionMatrix(lower);
        }
        const index = Collections.binarySearchNumeric(this.confusionMatrices.map((cm) => cm.getAllNegative()), this._getConfusionMatrix(lower).getTotal() - config.capacity, Collections.SearchMode.NearestAbove, lower);
        let confusionMatrix = this._getConfusionMatrix(index);
        // can we drop the threshold further
        if (index > lower) {
            const nextConfusionMatrix = this._getConfusionMatrix(index - 1);
            confusionMatrix = ConfusionMatrixFactory._adjustToCapacity(confusionMatrix, nextConfusionMatrix, config.capacity);
        }
        this.cache[config.capacity] = confusionMatrix;
        return confusionMatrix;
    }
    static _createDefault(confusionMatrices, cbm) {
        let maxImpact = -Infinity;
        let maxIndex = -1;
        let maxConfusionMatrix = confusionMatrices[maxIndex];
        for (let index = 0; index < confusionMatrices.length; index++) {
            const impact = cbm !== undefined
                ? confusionMatrices[index].getImpact(cbm)
                : Math.abs(confusionMatrices[index].getMatthewsCorrelationCoefficient());
            if (impact > maxImpact) {
                maxImpact = impact;
                maxIndex = index;
                maxConfusionMatrix = confusionMatrices[index];
            }
        }
        if (maxIndex > 0) {
            const originalMaxIndex = maxIndex;
            const originalConfusionMatrix = maxConfusionMatrix;
            const nextConfusionMatrix = confusionMatrices[maxIndex - 1];
            const start = maxConfusionMatrix.getAllPositive() + 1;
            const end = nextConfusionMatrix.getAllPositive();
            const step = Math.max(Math.round((end - start) / 10), 1);
            for (let capacity = start; capacity < end; capacity += step) {
                const confusionMatrix = this._adjustToCapacity(originalConfusionMatrix, nextConfusionMatrix, capacity);
                const impact = cbm !== undefined
                    ? confusionMatrix.getImpact(cbm)
                    : Math.abs(confusionMatrix.getMatthewsCorrelationCoefficient());
                if (impact > maxImpact) {
                    maxImpact = impact;
                    maxConfusionMatrix = confusionMatrix;
                    maxIndex = originalMaxIndex - 1;
                }
            }
        }
        return maxConfusionMatrix;
    }
    static create(confusionMatrices, cbm) {
        const defaultConfusionMatrix = ConfusionMatrixFactory._createDefault(confusionMatrices, cbm);
        return new ConfusionMatrixFactory(confusionMatrices, defaultConfusionMatrix);
    }
}
export class ModelFactoryConfig {
    ignoredModels;
    setup;
    conversionGain;
    conversionCost;
    conversionMissed;
    conversionSaved;
    total;
    totalPossibleHits;
    processLimit;
    isQuickRun;
    currentROI;
    setupMode;
    hasSpecifiedProcessRatio;
    realityRatio;
    hasCostBenefit;
    constructor(ignoredModels, setup, conversionGain, conversionCost, conversionMissed, conversionSaved, total, totalPossibleHits, processLimit, isQuickRun, currentROI, setupMode, hasSpecifiedProcessRatio, realityRatio, hasCostBenefit) {
        this.ignoredModels = ignoredModels;
        this.setup = setup;
        this.conversionGain = conversionGain;
        this.conversionCost = conversionCost;
        this.conversionMissed = conversionMissed;
        this.conversionSaved = conversionSaved;
        this.total = total;
        this.totalPossibleHits = totalPossibleHits;
        this.processLimit = processLimit;
        this.isQuickRun = isQuickRun;
        this.currentROI = currentROI;
        this.setupMode = setupMode;
        this.hasSpecifiedProcessRatio = hasSpecifiedProcessRatio;
        this.realityRatio = realityRatio;
        this.hasCostBenefit = hasCostBenefit;
    }
    copy() {
        return new ModelFactoryConfig(this.ignoredModels, this.setup, this.conversionGain, this.conversionCost, this.conversionMissed, this.conversionSaved, this.total, this.totalPossibleHits, this.processLimit, this.isQuickRun, this.currentROI, this.setupMode, this.hasSpecifiedProcessRatio, this.realityRatio, this.hasCostBenefit);
    }
    equals(other) {
        if (other === undefined) {
            return false;
        }
        if ((this.ignoredModels === undefined && other.ignoredModels !== undefined) ||
            (this.ignoredModels !== undefined && other.ignoredModels === undefined)) {
            return false;
        }
        else if (this.ignoredModels !== undefined) {
            if (Object.keys(this.ignoredModels).some((key) => this.ignoredModels[key] != other.ignoredModels[key])) {
                return false;
            }
            else if (Object.keys(other.ignoredModels).some((key) => this.ignoredModels[key] != other.ignoredModels[key])) {
                return false;
            }
        }
        if ((this.setup === undefined && other.setup !== undefined) ||
            (this.setup !== undefined && other.setup === undefined)) {
            return false;
        }
        else if (this.setup !== undefined) {
            if (this.setup.truePositiveLabel != other.setup.truePositiveLabel ||
                this.setup.falsePositiveLabel != other.setup.falsePositiveLabel ||
                this.setup.trueNegativeLabel != other.setup.trueNegativeLabel ||
                this.setup.falseNegativeLabel != other.setup.falseNegativeLabel) {
                return false;
            }
        }
        return (this.conversionGain == other.conversionGain &&
            this.conversionCost == other.conversionCost &&
            this.conversionMissed == other.conversionMissed &&
            this.conversionSaved == other.conversionSaved &&
            this.total == other.total &&
            this.totalPossibleHits == other.totalPossibleHits &&
            this.processLimit == other.processLimit &&
            this.isQuickRun == other.isQuickRun &&
            this.currentROI == other.currentROI &&
            this.setupMode == other.setupMode &&
            this.hasSpecifiedProcessRatio == other.hasSpecifiedProcessRatio &&
            this.realityRatio == other.realityRatio &&
            this.hasCostBenefit == other.hasCostBenefit);
    }
    diff(other) {
        if (other === undefined) {
            console.log("Other: " + other);
        }
        if ((this.ignoredModels === undefined && other.ignoredModels !== undefined) ||
            (this.ignoredModels !== undefined && other.ignoredModels === undefined)) {
            console.log(`[ignoredModels]`, `This: `, this.ignoredModels, `Other: `, other.ignoredModels);
        }
        else if (this.ignoredModels !== undefined) {
            if (Object.keys(this.ignoredModels).some((key) => this.ignoredModels[key] != other.ignoredModels[key])) {
                console.log(`[ignoredModels]`, `This: `, this.ignoredModels, `Other: `, other.ignoredModels);
            }
            else if (Object.keys(other.ignoredModels).some((key) => this.ignoredModels[key] != other.ignoredModels[key])) {
                console.log(`[ignoredModels]`, `This: `, this.ignoredModels, `Other: `, other.ignoredModels);
            }
        }
        if ((this.setup === undefined && other.setup !== undefined) ||
            (this.setup !== undefined && other.setup === undefined)) {
            console.log(`[setup]`, `This: `, this.setup, `Other: `, other.setup);
        }
        else if (this.setup !== undefined) {
            if (this.setup.truePositiveLabel != other.setup.truePositiveLabel ||
                this.setup.falsePositiveLabel != other.setup.falsePositiveLabel ||
                this.setup.trueNegativeLabel != other.setup.trueNegativeLabel ||
                this.setup.falseNegativeLabel != other.setup.falseNegativeLabel) {
                console.log(`[setup]`, `This: `, this.setup, `Other: `, other.setup);
            }
        }
        if (this.conversionGain != other.conversionGain) {
            console.log(`[conversionGain] This: ${this.conversionGain} Other: ${other.conversionGain}`);
        }
        if (this.conversionCost != other.conversionCost) {
            console.log(`[conversionCost] This: ${this.conversionCost} Other: ${other.conversionCost}`);
        }
        if (this.conversionMissed != other.conversionMissed) {
            console.log(`[conversionMissed] This: ${this.conversionMissed} Other: ${other.conversionMissed}`);
        }
        if (this.conversionSaved != other.conversionSaved) {
            console.log(`[conversionSaved] This: ${this.conversionSaved} Other: ${other.conversionSaved}`);
        }
        if (this.total != other.total) {
            console.log(`[total] This: ${this.total} Other: ${other.total}`);
        }
        if (this.totalPossibleHits != other.totalPossibleHits) {
            console.log(`[totalPossibleHits] This: ${this.totalPossibleHits} Other: ${other.totalPossibleHits}`);
        }
        if (this.processLimit != other.processLimit) {
            console.log(`[processLimit] This: ${this.processLimit} Other: ${other.processLimit}`);
        }
        if (this.isQuickRun != other.isQuickRun) {
            console.log(`[isQuickRun] This: ${this.isQuickRun} Other: ${other.isQuickRun}`);
        }
        if (this.currentROI != other.currentROI) {
            console.log(`[currentROI] This: ${this.currentROI} Other: ${other.currentROI}`);
        }
        if (this.setupMode != other.setupMode) {
            console.log(`[setupMode] This: ${this.setupMode} Other: ${other.setupMode}`);
        }
        if (this.hasSpecifiedProcessRatio != other.hasSpecifiedProcessRatio) {
            console.log(`[hasSpecifiedProcessRatio] This: ${this.hasSpecifiedProcessRatio} Other: ${other.hasSpecifiedProcessRatio}`);
        }
        if (this.realityRatio != other.realityRatio) {
            console.log(`[realityRatio] This: ${this.realityRatio} Other: ${other.realityRatio}`);
        }
        if (this.hasCostBenefit != other.hasCostBenefit) {
            console.log(`[hasCostBenefit] This: ${this.hasCostBenefit} Other: ${other.hasCostBenefit}`);
        }
    }
    static create(ignoredModels, setup, conversionGain, conversionCost, conversionMissed, conversionSaved, total, totalPossibleHits, processLimit, isQuickRun, currentROI, setupMode, hideCosts, processRatio, useRatio = false, realityRatio, hasCostBenefit, costSliderRatio) {
        if (useRatio === true && costSliderRatio !== undefined) {
            conversionCost = conversionGain / costSliderRatio;
            conversionSaved = conversionGain / costSliderRatio;
        }
        if (hideCosts) {
            conversionSaved = 0;
            conversionMissed = 0;
        }
        let customLimit = processLimit;
        if (processRatio !== false) {
            customLimit = roundIfConfigured(Number(processRatio) * total);
        }
        const hasSpecifiedProcessRatio = customLimit != processLimit;
        if (hasSpecifiedProcessRatio) {
            processLimit = roundIfConfigured(Number(processRatio) * total);
        }
        return new ModelFactoryConfig(ignoredModels, setup, conversionGain, conversionCost, conversionMissed, conversionSaved, total, totalPossibleHits, processLimit, isQuickRun, currentROI, setupMode, hasSpecifiedProcessRatio, realityRatio, hasCostBenefit);
    }
}
export class ModelFactory {
    DEBUG = false;
    jobId;
    rawModels;
    models;
    specifiedModels;
    config;
    configSpecifiedRatio;
    capacityModels;
    capacityConfig;
    capacityBreakpoints;
    capacityModelsFast;
    capacityConfigFast;
    capacityBreakpointsFast;
    capacityModelsFastAll;
    capacityConfigFastAll;
    capacityBreakpointsFastAll;
    constructor(jobId, rawModels) {
        this.jobId = jobId;
        this.rawModels = rawModels;
    }
    static create(job) {
        const models = job.models;
        const hasPredictionHistograms = Object.values(models).some((model) => model.metadata && model.metadata.prediction_histogram);
        const rawModels = Object.values(models)
            .filter((m) => (m.metrics &&
            Object.keys(m.metrics).length > 0 &&
            m.metrics.sum_over_predict !== "NaN" &&
            m.metrics.sum_under_predict !== "NaN") ||
            m.metrics === undefined ||
            Object.keys(m.metrics).length === 0)
            .filter((m) => !hasPredictionHistograms ||
            (m.metadata &&
                m.metadata.prediction_histogram &&
                Object.keys(m.metadata.prediction_histogram.actual_negative).length > 0 &&
                Object.keys(m.metadata.prediction_histogram.actual_positive).length > 0));
        return new ModelFactory(job.id, rawModels);
    }
    getCapacityModelsFast(config, breakpoints, sendAll) {
        if (sendAll === false &&
            (this.capacityConfigFast === undefined ||
                !this.capacityConfigFast.equals(config) ||
                !isObjectsEqual(this.capacityBreakpointsFast, breakpoints)) &&
            config !== undefined) {
            if (this.DEBUG) {
                console.log(`[${this.jobId}] CACHE MISS - CAPACITY_FAST`);
                if (this.capacityConfigFast !== undefined) {
                    this.capacityConfigFast.diff(config);
                }
            }
            this.capacityConfigFast = config;
            this.capacityBreakpointsFast = breakpoints;
            const capacityModels = {};
            Object.keys(breakpoints).forEach((b) => {
                const capacityRatio = breakpoints[b];
                const capacityConfig = config.copy();
                capacityConfig.processLimit = roundIfConfigured(capacityConfig.total * capacityRatio);
                capacityConfig.hasSpecifiedProcessRatio = true;
                capacityModels[b] = this._getModelsFast(this.rawModels, capacityConfig, breakpoints[b]).sort((a, b) => b.roi - a.roi)[0];
                /*
                        capacityModels[b] = this._getModels(
                            this.rawModels,
                            capacityConfig,
                            breakpoints[b]
                        );
                        */
            });
            const models = this.getModels(config);
            if (models && models[0]) {
                const capacityRatio = models[0].maxIteration.processed;
                const capacityConfig = config.copy();
                const processed = `${Math.round(capacityRatio * 100)}%`;
                capacityConfig.processLimit = roundIfConfigured(capacityConfig.total * capacityRatio);
                capacityConfig.hasSpecifiedProcessRatio = true;
                capacityModels[processed] = this._getModelsFast(this.rawModels, capacityConfig, processed).sort((a, b) => b.roi - a.roi)[0];
            }
            this.capacityModelsFast = capacityModels;
        }
        if (sendAll === true &&
            (this.capacityConfigFastAll === undefined ||
                !this.capacityConfigFastAll.equals(config) ||
                !isObjectsEqual(this.capacityBreakpointsFastAll, breakpoints)) &&
            config !== undefined) {
            if (this.DEBUG) {
                console.log(`[${this.jobId}] CACHE MISS - CAPACITY_FAST_ALL`);
                if (this.capacityConfigFastAll !== undefined) {
                    this.capacityConfigFastAll.diff(config);
                }
            }
            this.capacityConfigFastAll = config;
            this.capacityBreakpointsFastAll = breakpoints;
            const capacityModels = {};
            Object.keys(breakpoints).forEach((b) => {
                const capacityRatio = breakpoints[b];
                const capacityConfig = config.copy();
                capacityConfig.processLimit = roundIfConfigured(capacityConfig.total * capacityRatio);
                capacityConfig.hasSpecifiedProcessRatio = true;
                capacityModels[b] = this._getModelsFast(this.rawModels, capacityConfig, breakpoints[b]).sort((a, b) => b.roi - a.roi);
                /*
                        capacityModels[b] = this._getModels(
                            this.rawModels,
                            capacityConfig,
                            breakpoints[b]
                        );
                        */
            });
            const models = this.getModels(config);
            if (models && models[0]) {
                const capacityRatio = models[0].maxIteration.processed;
                const capacityConfig = config.copy();
                const processed = `${Math.round(capacityRatio * 100)}%`;
                capacityConfig.processLimit = roundIfConfigured(capacityConfig.total * capacityRatio);
                capacityConfig.hasSpecifiedProcessRatio = true;
                capacityModels[processed] = this._getModelsFast(this.rawModels, capacityConfig, capacityRatio).sort((a, b) => b.roi - a.roi);
            }
            this.capacityModelsFastAll = capacityModels;
        }
        return sendAll ? this.capacityModelsFastAll : this.capacityModelsFast;
    }
    getCapacityModels(config, breakpoints) {
        if ((this.capacityConfig === undefined ||
            !this.capacityConfig.equals(config) ||
            !isObjectsEqual(this.capacityBreakpoints, breakpoints)) &&
            config !== undefined) {
            if (this.DEBUG) {
                console.log(`[${this.jobId}] CACHE MISS - CAPACITY`);
                if (this.capacityConfig !== undefined) {
                    this.capacityConfig.diff(config);
                }
            }
            this.capacityConfig = config;
            this.capacityBreakpoints = breakpoints;
            const capacityModels = {};
            Object.keys(breakpoints).forEach((b) => {
                const capacityRatio = breakpoints[b];
                const capacityConfig = config.copy();
                capacityConfig.processLimit = roundIfConfigured(capacityConfig.total * capacityRatio);
                capacityConfig.hasSpecifiedProcessRatio = true;
                capacityModels[b] = this._getModels(this.rawModels, capacityConfig, breakpoints[b]);
            });
            const models = this.getModels(config);
            if (models && models[0]) {
                const processed = `${Math.round(models[0].maxIteration.processed * 100)}%`;
                capacityModels[processed] = models;
            }
            this.capacityModels = capacityModels;
        }
        return this.capacityModels;
    }
    getModels(config) {
        if (config.hasSpecifiedProcessRatio &&
            (this.configSpecifiedRatio === undefined || !this.configSpecifiedRatio.equals(config))) {
            if (this.DEBUG) {
                console.log(`[${this.jobId}] CACHE MISS - SINGLE_SPECIFIED`);
                if (this.configSpecifiedRatio !== undefined) {
                    this.configSpecifiedRatio.diff(config);
                }
            }
            this.configSpecifiedRatio = config;
            this.specifiedModels = this._getModels(this.rawModels, config, undefined);
        }
        else if (config.hasSpecifiedProcessRatio !== true &&
            (this.config === undefined || !this.config.equals(config)) &&
            config !== undefined) {
            if (this.DEBUG) {
                console.log(`[${this.jobId}] CACHE MISS - SINGLE`);
                if (this.config !== undefined) {
                    this.config.diff(config);
                }
            }
            this.config = config;
            this.models = this._getModels(this.rawModels, config, undefined);
        }
        return config.hasSpecifiedProcessRatio ? this.specifiedModels : this.models;
    }
    _getModelsFast(rawModels, config, ratio) {
        return rawModels
            .filter((m) => !(config.ignoredModels && config.ignoredModels[m.id]) &&
            (Object.keys(m.iterations).length > 0 || Object.keys(m.metrics).length > 0))
            .map((model) => {
            let retModel = {
                id: model.id,
                label: model.label,
                roi: undefined,
                confusionMatrix: undefined,
                metrics: model.metrics,
                features: model.features,
                metadata: model.metadata
            };
            if (model && model.iterations && Object.keys(model.iterations).length > 0) {
                const cbm = new CostBenefitMatrix(config.conversionGain, config.conversionSaved, config.conversionCost, config.conversionMissed);
                const cmf = model.cmff.getConfusionMatrixFactory(config.total, config.totalPossibleHits, cbm);
                let confusionMatrix = cmf.getConfusionMatrix({
                    capacity: config.processLimit
                });
                const roi = confusionMatrix.getImpact(cbm);
                retModel.roi = roi;
                retModel.confusionMatrix = confusionMatrix;
                const processed = confusionMatrix ? confusionMatrix.getAllPositive() / config.processLimit : 0;
                const processMissed = confusionMatrix
                    ? (config.processLimit - confusionMatrix.getAllPositive()) / config.processLimit
                    : 0;
                const bestResource = confusionMatrix ? confusionMatrix.getAllPositive() : 0;
                retModel.maxIteration = {
                    confusionMatrixFactory: cmf,
                    confusionMatrix: confusionMatrix,
                    threshold: confusionMatrix ? Math.min(Math.max(confusionMatrix.threshold, 0), 1) : 0,
                    recall: confusionMatrix ? confusionMatrix.getRecall() : 0,
                    precision: confusionMatrix ? confusionMatrix.getPrecision() : 0,
                    totalWins: confusionMatrix ? confusionMatrix.getTruePositive() : 0,
                    truePositive: confusionMatrix ? confusionMatrix.getTruePositive() : 0,
                    falsePositive: confusionMatrix ? confusionMatrix.getFalsePositive() : 0,
                    falseNegative: confusionMatrix ? confusionMatrix.getFalseNegative() : 0,
                    trueNegative: confusionMatrix ? confusionMatrix.getTrueNegative() : 0,
                    roi,
                    processMissed,
                    accuracy: confusionMatrix ? confusionMatrix.getAccuracy() : 0,
                    averageWinRate: confusionMatrix ? confusionMatrix.getPrecision() : 0,
                    processed: processed,
                    displayImpact: roi,
                    bestResource: bestResource,
                    ratio: ratio
                };
                retModel.hasFeatures =
                    model &&
                        model.features &&
                        model.features.percentages !== undefined &&
                        Object.keys(model.features.percentages).length > 0
                        ? true
                        : false;
                retModel.featureInfo = this._getFeaturesWithFallback(rawModels, model);
            }
            if (model.metrics && Object.keys(model.metrics).length > 0) {
                retModel.metrics = {};
                if (retModel.metrics.max_h_mean_absolute_error !== undefined) {
                    retModel.metrics.mean_absolute_error = retModel.metrics.max_h_mean_absolute_error;
                }
                if (retModel.metrics.max_h_count_over_predict !== undefined) {
                    retModel.metrics.count_over_predict = retModel.metrics.max_h_count_over_predict;
                }
                if (retModel.metrics.max_h_count_under_predict !== undefined) {
                    retModel.metrics.count_under_predict = retModel.metrics.max_h_count_under_predict;
                }
                if (retModel.metrics.max_h_sum_over_predict !== undefined) {
                    retModel.metrics.sum_over_predict = retModel.metrics.max_h_sum_over_predict;
                }
                if (retModel.metrics.max_h_sum_under_predict !== undefined) {
                    retModel.metrics.sum_under_predict = retModel.metrics.max_h_sum_under_predict;
                }
                retModel.metrics.conversionGain = config.conversionGain;
                retModel.metrics.conversionCost = config.conversionCost;
                retModel.metrics.overImpact =
                    Math.abs(retModel.metrics.sum_over_predict *
                        Math.abs(config.conversionGain !== undefined ? config.conversionGain : 1)) * config.realityRatio;
                retModel.metrics.underImpact =
                    Math.abs(retModel.metrics.sum_under_predict *
                        Math.abs(config.conversionCost !== undefined ? config.conversionCost : 1)) * config.realityRatio;
            }
            return retModel;
        });
    }
    _getModels(rawModels, config, ratio) {
        let retModelsArray = rawModels
            .filter((m) => !(config.ignoredModels && config.ignoredModels[m.id]) &&
            (Object.keys(m.iterations).length > 0 || Object.keys(m.metrics).length > 0))
            .map((model) => {
            let maxROI = -Infinity;
            let iterationIndex = 0;
            let checkMax = undefined;
            // POTENTIAL STATE MUTATION
            let retModel = {
                ...model,
                metrics: { ...model.metrics },
                features: { ...model.features },
                metadata: { ...model.metadata }
            };
            retModel.iterations = Object.keys(model.iterations).map((iKey, index) => {
                const cbm = new CostBenefitMatrix(config.conversionGain, config.conversionSaved, config.conversionCost, config.conversionMissed);
                const cmf = model.cmff.getConfusionMatrixFactory(config.total, config.totalPossibleHits, cbm);
                const constrainedResult = cmf.getConfusionMatrix({
                    capacity: config.processLimit
                });
                const unconstrainedResult = cmf.getConfusionMatrix();
                const cms = {
                    constrained: constrainedResult,
                    unconstrained: unconstrainedResult,
                    confusionMatrixFactory: cmf
                };
                let confusionMatrix = cms.unconstrained;
                const constrainedConfusionMatrix = cms.constrained;
                const processMissed = confusionMatrix
                    ? (config.processLimit - confusionMatrix.getAllPositive()) / config.processLimit
                    : 0;
                const bestResource = confusionMatrix ? confusionMatrix.getAllPositive() : 0;
                const showConstraint = confusionMatrix
                    ? config.hasSpecifiedProcessRatio || confusionMatrix.getAllPositive() > config.processLimit
                    : false;
                if (showConstraint === true) {
                    confusionMatrix = constrainedConfusionMatrix;
                }
                const roi = confusionMatrix ? confusionMatrix.getImpact(cbm) : 0;
                const constraintROI = constrainedConfusionMatrix ? constrainedConfusionMatrix.getImpact(cbm) : 0;
                const improvement = config.setupMode === 3 || config.setupMode === 1
                    ? undefined
                    : showConstraint
                        ? constraintROI - config.currentROI
                        : roi - config.currentROI;
                const processed = confusionMatrix ? confusionMatrix.getAllPositive() / config.processLimit : 0;
                let retIteration = {
                    confusionMatrixFactory: cms.confusionMatrixFactory,
                    confusionMatrix: confusionMatrix,
                    threshold: confusionMatrix ? Math.min(Math.max(confusionMatrix.threshold, 0), 1) : 0,
                    recall: confusionMatrix ? confusionMatrix.getRecall() : 0,
                    precision: confusionMatrix ? confusionMatrix.getPrecision() : 0,
                    totalWins: confusionMatrix ? confusionMatrix.getTruePositive() : 0,
                    truePositive: confusionMatrix ? confusionMatrix.getTruePositive() : 0,
                    falsePositive: confusionMatrix ? confusionMatrix.getFalsePositive() : 0,
                    falseNegative: confusionMatrix ? confusionMatrix.getFalseNegative() : 0,
                    trueNegative: confusionMatrix ? confusionMatrix.getTrueNegative() : 0,
                    improvement,
                    roi,
                    constraintROI,
                    showConstraint,
                    processMissed,
                    accuracy: confusionMatrix ? confusionMatrix.getAccuracy() : 0,
                    averageWinRate: confusionMatrix ? confusionMatrix.getPrecision() : 0,
                    processed: processed,
                    displayImpact: showConstraint ? constraintROI : roi,
                    bestResource: bestResource,
                    ratio: ratio,
                    index: index
                };
                if (maxROI === null || roi > maxROI) {
                    iterationIndex = index;
                    maxROI = roi;
                }
                checkMax = showConstraint ? constraintROI : roi;
                return retIteration;
            });
            if (model.metrics && Object.keys(model.metrics).length > 0) {
                if (retModel.metrics.max_h_mean_absolute_error !== undefined) {
                    retModel.metrics.mean_absolute_error = retModel.metrics.max_h_mean_absolute_error;
                }
                if (retModel.metrics.max_h_count_over_predict !== undefined) {
                    retModel.metrics.count_over_predict = retModel.metrics.max_h_count_over_predict;
                }
                if (retModel.metrics.max_h_count_under_predict !== undefined) {
                    retModel.metrics.count_under_predict = retModel.metrics.max_h_count_under_predict;
                }
                if (retModel.metrics.max_h_sum_over_predict !== undefined) {
                    retModel.metrics.sum_over_predict = retModel.metrics.max_h_sum_over_predict;
                }
                if (retModel.metrics.max_h_sum_under_predict !== undefined) {
                    retModel.metrics.sum_under_predict = retModel.metrics.max_h_sum_under_predict;
                }
                retModel.metrics.conversionGain = config.conversionGain;
                retModel.metrics.conversionCost = config.conversionCost;
                retModel.metrics.overImpact =
                    Math.abs(retModel.metrics.sum_over_predict *
                        Math.abs(config.conversionGain !== undefined ? config.conversionGain : 1)) * config.realityRatio;
                retModel.metrics.underImpact =
                    Math.abs(retModel.metrics.sum_under_predict *
                        Math.abs(config.conversionCost !== undefined ? config.conversionCost : 1)) * config.realityRatio;
            }
            if (model.metadata && model.metadata.metrics) {
                retModel.metadata = model.metadata.metrics;
            }
            const maxIteration = retModel.iterations[iterationIndex];
            if (maxIteration !== undefined) {
                const truePositiveVal = maxIteration.truePositive * config.conversionGain;
                const trueNegativeVal = maxIteration.trueNegative * config.conversionSaved;
                const falsePositiveVal = maxIteration.falsePositive * config.conversionCost;
                const falseNegativeVal = maxIteration.falseNegative * config.conversionMissed;
                const totalVal = truePositiveVal + trueNegativeVal;
                retModel.maxIteration = {
                    ...maxIteration,
                    truePositiveVal,
                    trueNegativeVal,
                    falsePositiveVal,
                    falseNegativeVal,
                    totalVal
                };
                retModel.maxIteration.revenue = maxIteration.truePositive * config.conversionGain;
                retModel.maxIteration.cost = (maxIteration.truePositive + maxIteration.falsePositive) * config.conversionCost;
                retModel.type = "ROI";
                retModel.checkMaxAbs = maxIteration.showConstraint
                    ? checkMax
                    : Math.max(Math.abs(truePositiveVal), Math.abs(truePositiveVal + trueNegativeVal), Math.abs(truePositiveVal + trueNegativeVal + falsePositiveVal), Math.abs(truePositiveVal + trueNegativeVal + falsePositiveVal + falseNegativeVal));
                retModel.checkMax = checkMax;
            }
            else {
                // TODO: Calculate impact with the actual cost/benefits
                const hasCostBenefit = config.hasCostBenefit;
                retModel.maxIteration = {};
                if ((config.isQuickRun && hasCostBenefit) ||
                    (config.conversionGain !== undefined && config.conversionMissed !== undefined)) {
                    retModel.checkMax = -1 * (retModel.metrics.overImpact + retModel.metrics.underImpact);
                    retModel.meanAbsoluteError = -1 * retModel.metrics.mean_absolute_error;
                }
                else {
                    retModel.checkMax = config.isQuickRun
                        ? -1 * retModel.metrics.mean_absolute_error
                        : -1 * (retModel.metrics.overImpact + retModel.metrics.underImpact);
                }
                retModel.checkMaxAbs = retModel.checkMax;
                retModel.type = "MAE";
            }
            retModel.hasFeatures =
                model &&
                    model.features &&
                    model.features.percentages !== undefined &&
                    Object.keys(model.features.percentages).length > 0
                    ? true
                    : false;
            retModel.featureInfo = this._getFeaturesWithFallback(rawModels, model);
            retModel.roi = retModel.checkMax;
            return retModel;
        });
        /*
            const largest =
                retModelsArray &&
                    retModelsArray.length === 1 &&
                    retModelsArray[0].id === "Current State"
                    ? undefined
                    : Math.max.apply(
                        null,
                        retModelsArray.map((m) => Math.abs(m.checkMaxAbs))
                    );
            */
        retModelsArray = retModelsArray.sort(function (a, b) {
            return b.checkMax - a.checkMax;
        });
        return retModelsArray;
    }
    _getFeaturesWithFallback(rawModels, model) {
        let retFeatures = model && model.features ? model.features.percentages : {};
        if (retFeatures === undefined || Object.keys(retFeatures).length === 0) {
            const modelsWithFeatures = rawModels.filter((m) => m.features && m.features.percentages && Object.keys(m.features.percentages).length > 0);
            if (modelsWithFeatures.length > 0) {
                retFeatures = modelsWithFeatures[0].features.percentages;
            }
        }
        return retFeatures
            ? Object.keys(retFeatures)
                .map((fKey) => {
                const feature = retFeatures[fKey];
                const score = feature.percentage !== undefined
                    ? feature.percentage * 100
                    : feature.weight !== undefined
                        ? feature.weight
                        : feature;
                return {
                    // This is A LOT of data we're duplicating and storing
                    //   ...feature,
                    label: fKey,
                    score: score
                };
            })
                .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))
            : [];
    }
    _buildBars(config, model) {
        const maxIteration = model.maxIteration;
        const metrics = model.metrics;
        const isContinuous = metrics && Object.keys(metrics).length > 0;
        const isQuickRun = config.isQuickRun;
        const bars = isContinuous
            ? [
                {
                    value: metrics.overImpact,
                    label: "Overestimate Rel. Impact",
                    tooltipLabel: "Impact",
                    color: metrics.overImpact > metrics.underImpact ? "rgb(235, 51, 43)" : "rgb(215, 164, 170)"
                },
                {
                    value: metrics.underImpact,
                    label: "Underestimate Rel. Impact",
                    tooltipLabel: "Impact",
                    color: metrics.underImpact > metrics.overImpact ? "rgb(235, 51, 43)" : "rgb(215, 164, 170)"
                }
            ]
            : isQuickRun
                ? [
                    {
                        value: maxIteration.truePositive,
                        label: config.setup.truePositiveLabel,
                        tooltipLabel: "Count"
                    },
                    {
                        value: maxIteration.falsePositive,
                        label: config.setup.falsePositiveLabel,
                        tooltipLabel: "Count",
                        color: maxIteration.falsePositive > maxIteration.falseNegative ? "rgb(235, 51, 43)" : "rgb(215, 164, 170)"
                    },
                    {
                        value: maxIteration.trueNegative,
                        label: config.setup.trueNegativeLabel,
                        tooltipLabel: "Count"
                    },
                    {
                        value: maxIteration.falseNegative,
                        label: config.setup.falseNegativeLabel,
                        tooltipLabel: "Count",
                        color: maxIteration.falseNegative > maxIteration.falsePositive ? "rgb(235, 51, 43)" : "rgb(215, 164, 170)"
                    }
                ]
                : [
                    {
                        value: maxIteration.truePositiveVal,
                        label: config.setup.truePositiveLabel,
                        count: maxIteration.truePositive
                    },
                    {
                        value: maxIteration.falsePositiveVal,
                        label: config.setup.falsePositiveLabel,
                        count: maxIteration.falsePositive
                    },
                    {
                        value: maxIteration.trueNegativeVal,
                        label: config.setup.trueNegativeLabel,
                        count: maxIteration.trueNegative
                    },
                    {
                        value: maxIteration.falseNegativeVal,
                        label: config.setup.falseNegativeLabel,
                        count: maxIteration.falseNegative
                    }
                ];
        return bars;
    }
}
